/* eslint-disable */
import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ShoppingBag,
  CheckCircle,
  XCircle,
  ShoppingCart,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { useTranslation } from "react-i18next";
export default function DashboardCards() {
  let orders = useSelector((state) => state.OrdersReducer);
  orders = orders.filter((order) => order.type != "digitalProducts");

  const normalizeTimestampToDate = (value) => {
    if (!value) return null;

    if (value instanceof Date) {
      return value;
    }

    if (typeof value?.toDate === "function") {
      const convertedDate = value.toDate();
      return convertedDate instanceof Date && !Number.isNaN(convertedDate.getTime())
        ? convertedDate
        : null;
    }

    if (typeof value?.seconds === "number") {
      return new Date(value.seconds * 1000);
    }

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const getLatestOrderActivityDate = (order) => {
    const orderCreationDate = normalizeTimestampToDate(order.timeStamp);
    const statusTimelineDates = Array.isArray(order.statusTimeline)
      ? order.statusTimeline
          .map((entry) => normalizeTimestampToDate(entry?.timestamp))
          .filter(Boolean)
      : [];

    if (statusTimelineDates.length === 0) {
      return orderCreationDate;
    }

    return statusTimelineDates.reduce(
      (latestDate, currentDate) =>
        currentDate > latestDate ? currentDate : latestDate,
      statusTimelineDates[0]
    );
  };

  const getTimestamps = () => {
    const now = new Date();
    const startOfToday = new Date(now);  
    startOfToday.setHours(0, 0, 0, 0);
    const endOfToday = new Date();

    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const endOfYesterday = new Date(startOfToday);

    const startOfLast30Days = new Date(startOfToday);
    startOfLast30Days.setDate(startOfLast30Days.getDate() - 30);

    return {
      startOfToday,
      endOfToday,
      startOfYesterday,
      endOfYesterday,
      startOfLast30Days,
    };
  };
  const { t, i18n } = useTranslation();
  const {
    startOfToday,
    endOfToday,
    startOfYesterday,
    endOfYesterday,
    startOfLast30Days,
  } =
    getTimestamps();

  const recentOrders = orders.filter((order) => {
    const orderLastActivityDate = getLatestOrderActivityDate(order);
    if (!orderLastActivityDate) return false;
    return (
      orderLastActivityDate >= startOfLast30Days &&
      orderLastActivityDate <= endOfToday
    );
  });

  const todayOrders = recentOrders.filter((order) => {
    const orderLastActivityDate = getLatestOrderActivityDate(order);
    if (!orderLastActivityDate) return false;
    return orderLastActivityDate >= startOfToday && orderLastActivityDate <= endOfToday;
  });

  const yesterdayOrders = recentOrders.filter((order) => {
    const orderLastActivityDate = getLatestOrderActivityDate(order);
    if (!orderLastActivityDate) return false;
    return (
      orderLastActivityDate >= startOfYesterday &&
      orderLastActivityDate <= endOfYesterday
    );
  });

  useEffect(() => {
    const countByStatus = (list) =>
      list.reduce(
        (acc, order) => {
          const status = String(order?.deleveryStatus ?? "unknown");
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        },
        {}
      );

    const sampleOrders = orders.slice(0, 20).map((order) => ({
      id: order.id,
      deleveryStatus: order.deleveryStatus,
      timeStamp: normalizeTimestampToDate(order.timeStamp)?.toISOString() || null,
      latestActivity: getLatestOrderActivityDate(order)?.toISOString() || null,
      timelineLength: Array.isArray(order.statusTimeline)
        ? order.statusTimeline.length
        : 0,
      latestTimelineStatus:
        Array.isArray(order.statusTimeline) && order.statusTimeline.length > 0
          ? order.statusTimeline[order.statusTimeline.length - 1]?.status
          : null,
    }));

  }, [
    orders,
    recentOrders,
    todayOrders,
    yesterdayOrders,
    startOfLast30Days,
    startOfToday,
    endOfToday,
    startOfYesterday,
    endOfYesterday,
  ]);

  const calculateTrend = (today, yesterday) => {
    if (yesterday === 0) return "N/A";
    const trend = ((today - yesterday) / yesterday) * 100;
    return `${trend.toFixed(1)}%`;
  };

  const isValidatedOrder = (order) => {
    const status = String(order?.deleveryStatus ?? "");
    return status === "3" || status === "4";
  };

  const data = useMemo(
    () => [
      {
        title: t("dashboard.cards.1.title"),
        value:
          todayOrders.filter((order) => order.deleveryStatus === "1").length ||
          0,
        yesterdayValue: yesterdayOrders.length || 0,
        icon: <ShoppingBag size={20} />,
        link: "/orders?today=1",
        color: "purple",
      },
      {
        title: t("dashboard.cards.2.title"),
        value: todayOrders.filter((order) => isValidatedOrder(order)).length || 0,
        yesterdayValue:
          yesterdayOrders.filter((order) => isValidatedOrder(order)).length ||
          0,
        icon: <CheckCircle size={20} />,
        link: "/orders?today=3",
        color: "teal",
      },
      {
        title: t("dashboard.cards.3.title"),
        value:
          todayOrders.filter((order) => order.deleveryStatus === "6").length ||
          0,
        yesterdayValue:
          yesterdayOrders.filter((order) => order.deleveryStatus === "6")
            .length || 0,
        icon: <XCircle size={20} />,
        link: "/orders?today=6",
        color: "rose",
      },
      {
        title: t("dashboard.cards.4.title"),
        value:
          todayOrders.filter((order) => order.deleveryStatus === "20").length,
        yesterdayValue:
          yesterdayOrders.filter((order) => order.deleveryStatus === "20")
            .length || 0,
        icon: <ShoppingCart size={20} />,
        link: "/Ordersabndn",
        color: "amber",
      },
    ],
    [todayOrders, yesterdayOrders]
  );

  // Map color names to specific tailwind classes
  const colorClasses = {
    purple: {
      bg: "bg-purple-500",
      text: "text-[#9e68ba]",
      light: "bg-[#fcf6ff]",
      border: "border-[#fbf4ff]",
    },
    teal: {
      bg: "bg-purple-500",
      text: "text-[#9e68ba]",
      light: "bg-[#fcf6ff]",
      border: "border-[#fbf4ff]",
    },
    rose: {
      bg: "bg-purple-500",
      text: "text-[#9e68ba]",
      light: "bg-[#fcf6ff]",
      border: "border-[#fbf4ff]",
    },
    amber: {
      bg: "bg-purple-500",
      text: "text-[#9e68ba]",
      light: "bg-[#fcf6ff]",
      border: "border-[#fbf4ff]",
    },
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4">
      {data.map((item, index) => {
        const trend = calculateTrend(item.value, item.yesterdayValue);
        const trendUp = item.value >= item.yesterdayValue;
        const trendColor = trendUp ? "text-green-500" : "text-red-500";
        const colorClass = colorClasses[item.color];

        return (
          <Link
            to={item.link}
            key={index}
            className={`block bg-white dark:!bg-[#181919] rounded-lg border ${colorClass.border} dark:!border-[#3c3a3d] overflow-hidden group`}
          >
            {/* Simpler header with item title and icon */}
            <div className="flex justify-between items-center p-4 border-b border-gray-100 dark:border-[#3c3a3d]">
              <h3 className="font-medium text-xl text-gray-700 dark:text-white">{item.title}</h3>
              <div
                className={`p-2 rounded-full ${colorClass.light} ${colorClass.text}`}
              >
                {item.icon}
              </div>
            </div>

            {/* Main content with large number and trend */}
            <div className="p-4">
              <div className="flex items-end justify-between">
                <div className="text-3xl font-bold dark:text-white">{item.value}</div>

                {trend !== "N/A" && (
                  <div className={`flex items-center ${trendColor} text-sm`}>
                    {trendUp ? (
                      <ArrowUp size={14} className="ml-1" />
                    ) : (
                      <ArrowDown size={14} className="ml-1" />
                    )}
                    {trend}
                  </div>
                )}
              </div>

              {/* Yesterday comparison */}
              <div className="mt-2 text-xs text-gray-500 dark:text-white">
                {t("dashboard.cards.yesterday")} {item.yesterdayValue}
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
